import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { companyProfileApi } from '@/services/api';
import { toast } from 'sonner';
import { Building2, Plus, Trash2, Users, Upload, ImageIcon } from 'lucide-react';
import type { CompanyProfile, TeamMember } from '@/types';

export function CompanyProfileSettings() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    name: '', legal_name: '', tagline: '',
    street: '', city: '', postal_code: '', country: '',
    email: '', phone: '', website: '',
    tax_id: '', registry_info: '', ceo_name: '',
    offer_prefix: 'SO', default_binding_days: 30,
    default_payment_terms: '', standard_terms: '',
  });

  const [newMember, setNewMember] = useState({
    name: '', title: '', role: '', email: '', phone: '', expertise: '', bio: '',
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await companyProfileApi.get();
      if (data) {
        setProfile(data);
        if (data.logo_url) setLogoUrl(data.logo_url);
        setForm({
          name: data.name || '',
          legal_name: data.legal_name || '',
          tagline: data.tagline || '',
          street: data.street || '',
          city: data.city || '',
          postal_code: data.postal_code || '',
          country: data.country || '',
          email: data.email || '',
          phone: data.phone || '',
          website: data.website || '',
          tax_id: data.tax_id || '',
          registry_info: data.registry_info || '',
          ceo_name: data.ceo_name || '',
          offer_prefix: data.offer_prefix || 'SO',
          default_binding_days: data.default_binding_days || 30,
          default_payment_terms: data.default_payment_terms || '',
          standard_terms: data.standard_terms || '',
        });
        const teamData = await companyProfileApi.listTeam(data.id);
        setTeam(teamData || []);
      }
    } catch {
      // No profile yet
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    try {
      setSaving(true);
      if (profile) {
        const updated = await companyProfileApi.update(profile.id, form);
        setProfile(updated);
        toast.success(t('proposals.profileUpdated'));
      } else {
        const created = await companyProfileApi.create(form);
        setProfile(created);
        toast.success(t('proposals.profileCreated'));
      }
    } catch {
      toast.error(t('proposals.profileSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profile) return;
    if (file.size > 2 * 1024 * 1024) { toast.error(t('proposals.logoSizeError')); return; }
    try {
      setLogoUploading(true);
      const result = await companyProfileApi.uploadLogo(profile.id, file);
      setLogoUrl(result.logo_url);
      toast.success(t('proposals.logoUploaded'));
    } catch {
      toast.error(t('proposals.logoUploadFailed'));
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const addMember = async () => {
    if (!profile || !newMember.name.trim()) return;
    try {
      const member = await companyProfileApi.addTeamMember(profile.id, newMember);
      setTeam([...team, member]);
      setNewMember({ name: '', title: '', role: '', email: '', phone: '', expertise: '', bio: '' });
      toast.success(t('proposals.teamMemberAdded'));
    } catch {
      toast.error(t('proposals.teamMemberAddFailed'));
    }
  };

  const removeMember = async (id: number) => {
    try {
      await companyProfileApi.deleteTeamMember(id);
      setTeam(team.filter(m => m.id !== id));
      toast.success(t('proposals.teamMemberRemoved'));
    } catch {
      toast.error(t('proposals.teamMemberRemoveFailed'));
    }
  };

  if (loading) return <div className="text-muted-foreground p-4">{t('proposals.loadingProfile')}</div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {t('proposals.companyProfile')}
          </CardTitle>
          <CardDescription>
            {t('proposals.companyProfileDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center overflow-hidden bg-muted/20 shrink-0">
              {logoUrl ? (
                <img src={logoUrl} alt={t('proposals.companyLogo')} className="h-full w-full object-contain" />
              ) : (
                <ImageIcon className="h-6 w-6 text-muted-foreground/50" />
              )}
            </div>
            <div>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={handleLogoUpload}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => logoInputRef.current?.click()}
                disabled={!profile || logoUploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                {logoUploading ? t('common.uploading') : logoUrl ? t('proposals.changeLogo') : t('proposals.uploadLogo')}
              </Button>
              <p className="text-xs text-muted-foreground mt-1">{t('proposals.logoInfo')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('proposals.companyName')} *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t('proposals.companyNamePlaceholder')} />
            </div>
            <div className="space-y-2">
              <Label>{t('proposals.legalName')}</Label>
              <Input value={form.legal_name} onChange={e => setForm({ ...form, legal_name: e.target.value })} placeholder={t('proposals.legalNamePlaceholder')} />
            </div>
            <div className="space-y-2">
              <Label>{t('proposals.tagline')}</Label>
              <Input value={form.tagline} onChange={e => setForm({ ...form, tagline: e.target.value })} placeholder={t('proposals.taglinePlaceholder')} />
            </div>
            <div className="space-y-2">
              <Label>{t('proposals.ceoName')}</Label>
              <Input value={form.ceo_name} onChange={e => setForm({ ...form, ceo_name: e.target.value })} />
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-medium mb-3">{t('proposals.address')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('proposals.street')}</Label>
                <Input value={form.street} onChange={e => setForm({ ...form, street: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{t('proposals.city')}</Label>
                <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{t('proposals.postalCode')}</Label>
                <Input value={form.postal_code} onChange={e => setForm({ ...form, postal_code: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{t('proposals.country')}</Label>
                <Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-medium mb-3">{t('proposals.contact')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('proposals.email')}</Label>
                <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{t('proposals.phone')}</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{t('proposals.website')}</Label>
                <Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-medium mb-3">{t('proposals.legalOfferSettings')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('proposals.taxId')}</Label>
                <Input value={form.tax_id} onChange={e => setForm({ ...form, tax_id: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{t('proposals.registryInfo')}</Label>
                <Input value={form.registry_info} onChange={e => setForm({ ...form, registry_info: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{t('proposals.offerPrefix')}</Label>
                <Input value={form.offer_prefix} onChange={e => setForm({ ...form, offer_prefix: e.target.value })} placeholder="SO" />
              </div>
              <div className="space-y-2">
                <Label>{t('proposals.defaultBindingDays')}</Label>
                <Input type="number" value={form.default_binding_days} onChange={e => setForm({ ...form, default_binding_days: parseInt(e.target.value) || 30 })} />
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <Label>{t('proposals.defaultPaymentTerms')}</Label>
              <Textarea value={form.default_payment_terms} onChange={e => setForm({ ...form, default_payment_terms: e.target.value })} rows={2} placeholder={t('proposals.defaultPaymentTermsPlaceholder')} />
            </div>
            <div className="mt-4 space-y-2">
              <Label>{t('proposals.standardTerms')}</Label>
              <Textarea value={form.standard_terms} onChange={e => setForm({ ...form, standard_terms: e.target.value })} rows={4} placeholder={t('proposals.standardTermsPlaceholder')} />
            </div>
          </div>

          <Button onClick={saveProfile} disabled={saving || !form.name.trim()}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </CardContent>
      </Card>

      {profile && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t('proposals.teamMembers')}
            </CardTitle>
            <CardDescription>
              {t('proposals.teamMembersDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {team.map(member => (
              <div key={member.id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                <div>
                  <div className="font-medium">
                    {member.title && <span className="text-muted-foreground">{member.title} </span>}
                    {member.name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {member.role}{member.email && ` · ${member.email}`}
                  </div>
                  {member.expertise && (
                    <div className="text-xs text-muted-foreground mt-1">{member.expertise}</div>
                  )}
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeMember(member.id)} title={t('common.delete')}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}

            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium mb-3">{t('proposals.addMember')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input value={newMember.name} onChange={e => setNewMember({ ...newMember, name: e.target.value })} placeholder={`${t('proposals.memberName')} *`} />
                <Input value={newMember.title} onChange={e => setNewMember({ ...newMember, title: e.target.value })} placeholder={t('proposals.memberTitle')} />
                <Input value={newMember.role} onChange={e => setNewMember({ ...newMember, role: e.target.value })} placeholder={t('proposals.memberRole')} />
                <Input value={newMember.email} onChange={e => setNewMember({ ...newMember, email: e.target.value })} placeholder={t('proposals.email')} />
                <Input value={newMember.phone} onChange={e => setNewMember({ ...newMember, phone: e.target.value })} placeholder={t('proposals.phone')} />
                <Input value={newMember.expertise} onChange={e => setNewMember({ ...newMember, expertise: e.target.value })} placeholder={t('proposals.memberExpertise')} />
              </div>
              <div className="mt-3">
                <Textarea value={newMember.bio} onChange={e => setNewMember({ ...newMember, bio: e.target.value })} rows={2} placeholder={t('proposals.memberBio')} />
              </div>
              <Button className="mt-3" variant="outline" onClick={addMember} disabled={!newMember.name.trim()}>
                <Plus className="h-4 w-4 mr-2" /> {t('proposals.addMember')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
